import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, LayoutGrid, Copy, Trash2, Edit3, Eye, ArrowUpRight, Loader2 } from "lucide-react";
import { menuApi } from "@/lib/menuApi";
import { toast } from "sonner";
import { confirmDialog } from "@/components/ConfirmDialog";
import BackButton from "@/components/BackButton";
import { useAuth } from "@/context/AuthContext";

/**
 * Menu Designer landing page. Two primary actions surface at the top
 * (Create New / Templates) followed by a grid of the user's saved menus.
 * Everything reads from the /api/menus and /api/menu-templates endpoints
 * so nothing here duplicates state.
 */
export default function MenuDesigner() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [menus, setMenus] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([menuApi.listMenus(), menuApi.listTemplates()])
      .then(([m, t]) => {
        setMenus(m);
        setTemplates(t);
      })
      .catch(() => toast.error("Failed to load menus"))
      .finally(() => setLoading(false));
  }, []);

  async function handleDuplicate(m) {
    try {
      const clone = await menuApi.duplicateMenu(m.id);
      toast.success("Menu duplicated");
      nav(`/menu-designer/edit/${clone.id}`);
    } catch {
      toast.error("Could not duplicate");
    }
  }

  async function handleDelete(m) {
    const ok = await confirmDialog({ title: `Delete "${m.name}"?`, description: "This cannot be undone." });
    if (!ok) return;
    try {
      await menuApi.deleteMenu(m.id);
      setMenus((prev) => prev.filter((x) => x.id !== m.id));
      toast.success("Menu deleted");
    } catch {
      toast.error("Delete failed");
    }
  }

  return (
    <div className="space-y-10 slide-up" data-testid="menu-designer-page">
      <BackButton />
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Menu Designer</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold mt-1">Design Apollo-Branded Menus</h1>
        <p className="text-muted-foreground mt-3 max-w-2xl">
          Assemble polished, print-ready product menus using only approved Apollo backgrounds, products, and renders.
          Select a background, drop in your products, price them, and export.
        </p>
      </div>

      {/* Primary actions */}
      <div className="grid md:grid-cols-2 gap-4">
        <Link
          to="/menu-designer/new"
          className="group relative overflow-hidden border border-border rounded-sm bg-surface p-8 hover:bg-surface-elev transition"
          data-testid="menu-designer-create-new"
        >
          <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full" style={{ background: "radial-gradient(circle, rgba(251,201,12,0.25), transparent 60%)" }} />
          <Plus className="h-6 w-6 mb-6" />
          <div className="font-display text-2xl font-bold">Create New Menu</div>
          <div className="text-sm text-muted-foreground mt-2">Start from an Apollo background and assemble your menu.</div>
          <div className="mt-6 inline-flex items-center gap-1 text-xs font-mono uppercase tracking-[0.2em] group-hover:text-foreground transition">
            Start <ArrowUpRight className="h-3 w-3" />
          </div>
        </Link>

        <div className="border border-border rounded-sm bg-surface p-8">
          <LayoutGrid className="h-6 w-6 mb-6" />
          <div className="font-display text-2xl font-bold">Apollo Templates</div>
          <div className="text-sm text-muted-foreground mt-2">
            {templates.length} approved background{templates.length !== 1 ? "s" : ""} available
            {user?.role === "admin" && (
              <> · <Link to="/admin?tab=menu-templates" className="underline hover:text-foreground">manage</Link></>
            )}
          </div>
          <div className="mt-6 flex flex-wrap gap-2">
            {templates.slice(0, 4).map((t) => (
              <div key={t.id} className="w-20 h-24 rounded-sm border border-border overflow-hidden relative" style={{ background: t.background_color || "#0A0A0A" }}>
                <MiniLayout template={t} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Saved menus */}
      <section>
        <div className="flex items-end justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">My Saved Menus</div>
            <div className="font-display text-2xl font-bold mt-1">{menus.length} menu{menus.length !== 1 ? "s" : ""}</div>
          </div>
        </div>

        {loading ? (
          <div className="mt-6 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…</div>
        ) : menus.length === 0 ? (
          <div className="mt-6 border border-dashed border-border rounded-sm p-12 text-center text-sm text-muted-foreground" data-testid="menu-designer-empty">
            No menus yet. Click <span className="font-semibold text-foreground">Create New Menu</span> to build your first one.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-6">
            {menus.map((m) => (
              <MenuCard key={m.id} menu={m} onDuplicate={() => handleDuplicate(m)} onDelete={() => handleDelete(m)} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function MenuCard({ menu, onDuplicate, onDelete }) {
  const modified = menu.updated_at ? new Date(menu.updated_at).toLocaleDateString() : "";
  return (
    <div className="group border border-border rounded-sm overflow-hidden bg-surface" data-testid={`menu-card-${menu.id}`}>
      <Link to={`/menu-designer/edit/${menu.id}`} className="block aspect-[3/4] bg-muted relative">
        {menu.thumbnail_url ? (
          <img src={menu.thumbnail_url} alt={menu.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground font-mono text-[10px] uppercase tracking-widest">
            No preview yet
          </div>
        )}
      </Link>
      <div className="p-3">
        <div className="text-sm font-semibold truncate">{menu.name}</div>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">
          {menu.canvas_width}×{menu.canvas_height} · {modified}
        </div>
        <div className="mt-3 flex items-center gap-1.5">
          <Link
            to={`/menu-designer/edit/${menu.id}`}
            className="p-1.5 border border-border rounded-sm hover:bg-surface-elev transition"
            title="Edit"
            data-testid={`menu-edit-${menu.id}`}
          >
            <Edit3 className="h-3 w-3" />
          </Link>
          <Link
            to={`/menu-designer/edit/${menu.id}?preview=1`}
            className="p-1.5 border border-border rounded-sm hover:bg-surface-elev transition"
            title="Preview"
          >
            <Eye className="h-3 w-3" />
          </Link>
          <button
            onClick={onDuplicate}
            className="p-1.5 border border-border rounded-sm hover:bg-surface-elev transition"
            title="Duplicate"
            data-testid={`menu-duplicate-${menu.id}`}
          >
            <Copy className="h-3 w-3" />
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 border rounded-sm text-white transition hover:opacity-90 ml-auto"
            style={{ background: "#E15C2C", borderColor: "#E15C2C" }}
            title="Delete"
            data-testid={`menu-delete-${menu.id}`}
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Tiny structured preview for the Apollo Templates card on the landing page.
function MiniLayout({ template }) {
  const layout = template.layout || {};
  const scaleX = (v) => `${(v / template.canvas_width) * 100}%`;
  const scaleY = (v) => `${(v / template.canvas_height) * 100}%`;
  const accent = template.accent_color || "#FBC90C";
  return (
    <>
      {layout.header && (
        <div className="absolute flex items-center justify-center" style={{ left: scaleX(layout.header.x), top: scaleY(layout.header.y), width: scaleX(layout.header.w), height: scaleY(layout.header.h) }}>
          <div className="font-mono font-bold uppercase" style={{ color: accent, fontSize: 6, letterSpacing: 1 }}>APOLLO</div>
        </div>
      )}
      {(layout.slots || []).map((s) => (
        <div key={s.id} className="absolute rounded-sm" style={{ left: scaleX(s.x), top: scaleY(s.y), width: scaleX(s.w), height: scaleY(s.h), background: "rgba(255,255,255,0.08)", border: `1px solid ${accent}55` }} />
      ))}
    </>
  );
}

